<script setup lang="ts">
import {
  IconBaselineHome, IconBaselineMap,
  IconBaselineDirectionsCar,
  IconBaselineFormatListBulleted,
  IconSharpColorLens,
  IconBaselineBarChart,
  IconBaselineBarcode,
  IconBaselinePieChart,
  IconBaselineLocalShipping
} from '@iconify-prerendered/vue-ic';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { RouterLink } from 'vue-router';
import { useFeatureFlagStore } from '@/featureFlags/stores/featureFlagStore';
import { DefinedFeatureFlags } from 'shared/featureFlags/domain/definedFeatureFlags';
import { useAuthStore } from '@/auth/store/authStore';

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const featureFlagStore = useFeatureFlagStore();
const authStore = useAuthStore();
</script>

<template>
  <aside class="shadow">
    <RouterLink to="/home" v-tooltip="translations.sideMenu.home">
      <IconBaselineHome></IconBaselineHome>
    </RouterLink>
    <RouterLink to="/map" v-tooltip="translations.sideMenu.map">
      <IconBaselineMap></IconBaselineMap>
    </RouterLink>
    <RouterLink v-if="authStore.isAdmin" to="/vehicles" v-tooltip="translations.sideMenu.vehicles">
      <IconBaselineDirectionsCar></IconBaselineDirectionsCar>
    </RouterLink>
    <RouterLink v-if="authStore.isAdmin" to="/products" v-tooltip="translations.sideMenu.products">
      <IconBaselineFormatListBulleted></IconBaselineFormatListBulleted>
    </RouterLink>
    <RouterLink v-if="authStore.isAdmin" to="/delivery" v-tooltip="translations.sideMenu.deliveries">
      <IconBaselineLocalShipping></IconBaselineLocalShipping>
    </RouterLink>
    <RouterLink v-if="authStore.isAdmin" to="/styles" v-tooltip="translations.sideMenu.styles">
      <IconSharpColorLens></IconSharpColorLens>
    </RouterLink>
    <RouterLink to="/coils" v-tooltip="translations.sideMenu.coils"
      v-if="featureFlagStore.isFeatureEnabled(DefinedFeatureFlags.Coil) && authStore.isAdmin">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
        <path fill="currentColor"
          d="M12 16q1.671 0 2.836-1.164Q16 13.67 16 12t-1.164-2.835T12 8T9.165 9.165T8 12t1.165 2.836T12 16m.003 5q-1.867 0-3.51-.708q-1.643-.709-2.859-1.924t-1.925-2.856T3 12.003t.709-3.51Q4.417 6.85 5.63 5.634t2.857-1.925T11.997 3t3.51.709q1.643.708 2.859 1.922t1.925 2.857t.709 3.509t-.708 3.51t-1.924 2.859t-2.856 1.925t-3.509.709" />
      </svg>
    </RouterLink>
    <RouterLink to="/reports" v-tooltip="translations.sideMenu.reports">
      <IconBaselinePieChart></IconBaselinePieChart>
    </RouterLink>
    <RouterLink to="/packages" v-tooltip="translations.sideMenu.packages"
      v-if="featureFlagStore.isFeatureEnabled(DefinedFeatureFlags.Packages) && authStore.isAdmin">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
        <path fill="currentColor"
          d="M20.54 5.23L13.5 2.13a3.7 3.7 0 0 0-3 0L3.46 5.23A2.25 2.25 0 0 0 2 7.29v9.42a2.25 2.25 0 0 0 1.46 2.06l7.04 3.1c.96.42 2.04.42 3 0l7.04-3.1A2.25 2.25 0 0 0 22 16.71V7.29a2.25 2.25 0 0 0-1.46-2.06M12 4.15c.28 0 .56.06.82.17l6.38 2.82L12 10.29L4.8 7.14l6.38-2.82c.26-.11.54-.17.82-.17m-8 4.64l7 3.06v7.86l-6.64-2.92a.75.75 0 0 1-.36-.68zm9 10.92v-7.86l7-3.06v7.32a.75.75 0 0 1-.36.68z" />
      </svg>
    </RouterLink>
    <RouterLink v-if="authStore.isAdmin" to="/dashboards" v-tooltip="translations.sideMenu.dashboards">
      <IconBaselineBarChart></IconBaselineBarChart>
    </RouterLink>
    <RouterLink v-if="authStore.isAdmin" to="/productionorders" v-tooltip="translations.sideMenu.productionOrders">
      <IconBaselineBarcode></IconBaselineBarcode>
    </RouterLink>
    <RouterLink v-if="authStore.isAdmin" to="/systemsettings" v-tooltip="translations.sideMenu.systemsettings">
      <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 16 16" fill="currentColor">
        <path fill-rule="evenodd"
          d="M11.5 2a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3M9.05 3a2.5 2.5 0 0 1 4.9 0H16v1h-2.05a2.5 2.5 0 0 1-4.9 0H0V3zM4.5 7a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3M2.05 8a2.5 2.5 0 0 1 4.9 0H16v1H6.95a2.5 2.5 0 0 1-4.9 0H0V8zm9.45 4a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3m-2.45 1a2.5 2.5 0 0 1 4.9 0H16v1h-2.05a2.5 2.5 0 0 1-4.9 0H0v-1z" />
      </svg>
    </RouterLink>
  </aside>
</template>

<style scoped>
aside {
  grid-row: 2;
  grid-column: 1;
  display: flex;
  flex-direction: column;
  padding-inline: .5rem;
  padding-block: .5rem;
  row-gap: .5rem;
  z-index: 1;
}

a svg {
  width: 2rem;
  height: 2rem;
}

a:hover,
.router-link-active {
  color: var(--p-primary-500);
}

.p-dark aside {
  background-color: var(--p-surface-800);
}
</style>
